import { PickSnippetInput, SportslineWebComponent_PickSnippetParlayQuery } from '@lib/graphql-types';
import { Component, Host, h, Element, State } from '@stencil/core';
// import mockedData from './test/__mocks__/response';
import { generateHexId, isUndefinedOrNull } from '@utils/commonUtils';
import { fromPickSnippetToPickSnippetCardProps, getDataFromAttribute } from '@utils/sidelineUtils';
import { sportslineGraphQLClient } from '@lib/graphql-client';
import { observeVisibilityAndTrigger } from '@utils/html';
import { isGameAvailableForBetting } from '@utils/bettingUtils';
import { fromPickSnippetAttributeToTrackingImpressionWidgetDataBets, fromPickSnippetAttributeToTrackingViewedWidgetDataBets, toPickSnippetInput } from '@utils/pickSnippet';
import { eventsName, PickSnippetEventViewed, trackEvent } from '@utils/trackingUtils';

@Component({
  tag: 'sportsline-pick-snippet-parlay',
  styleUrl: 'sportsline-pick-snippet-parlay.css',
  shadow: true,
})
export class SportslinePickSnippetParlay {
  @State() data: SportslineWebComponent_PickSnippetParlayQuery['pickSnippetParlay'] = null;
  @State() loading: boolean = true;
  @State() error: string | null = null;
  @State() initialized: boolean = false;

  @Element() el: HTMLSportslinePickSnippetParlayElement;

  private instanceId: string = generateHexId();
  private version: string = '1.0.0';
  private name: string = 'parlay-picks';
  private client = sportslineGraphQLClient();

  private get getWidgetDataInfo() {
    return {
      name: this.name,
      instanceId: this.instanceId,
      version: this.version,
      ref: this.el,
    };
  }

  private trackError(error: Error) {
    this.error = `Error fetching data: ${error.message}`;
    console.log('Error fetching data:', this.error);
    console.warn('Error fetching data:', error);
    trackEvent({
      action: 'error',
      eventName: eventsName.martech,
      payload: {
        error: this.error,
      },
      widgetData: this.getWidgetDataInfo,
    });
  }

  private async fetch(input: PickSnippetInput[]) {
    this.initialized = true; // Mark as initialized to allow rendering
    this.loading = true;
    this.error = null;

    // fetching data, before loading the component
    if (!Boolean(input)) {
      console.warn('No input provided for SportslinePickSnippet');
      this.loading = false;
      this.error = 'No input provided for SportslinePickSnippet';
      return Promise.resolve();
    }
    return this.client.dataFetcher
      .SportslineWebComponent_PickSnippetParlay({
        input,
      })
      .then(response => {
        const data = response.data;
        this.data = data?.pickSnippetParlay;

        if (Boolean(response.errors?.length)) {
          throw new Error(`GraphQL errors: ${response.errors.map(error => error.message).join(', ')}`);
        }

        if (!Boolean(data?.pickSnippetParlay)) {
          throw new Error('No pick snippet parlay data found');
        }

        const pickAttributes = this.getDataFromAttribute; 
        const picks = this.data.legs; // leg of the parlays

        const bets = pickAttributes.map((dataAttr, index) =>
          fromPickSnippetAttributeToTrackingViewedWidgetDataBets({
            dataAttr,
            pick: picks[index],
            position: index + 1,
          }),
        );
        const event: PickSnippetEventViewed = {
          action: 'viewed',
          eventName: eventsName.martech,
          widgetData: {
            ...this.getWidgetDataInfo,
            bets,
          },
        };
        trackEvent(event);
      });
  }

  async componentWillLoad() {
    try {
      const input = this.getInput;
      if (this.getDataPickAttribute === null) {
        throw new Error('No data-pick attribute found on SportslinePickSnippetParlay component');
      }
      // Track the initial impression event
      trackEvent({
        action: 'impression',
        eventName: eventsName.martech,
        widgetData: {
          ...this.getWidgetDataInfo,
          bets: this.getDataFromAttribute.map((dataAttr, index) =>
            fromPickSnippetAttributeToTrackingImpressionWidgetDataBets({
              dataAttr,
              position: index + 1,
            }),
          ),
        },
      });
      observeVisibilityAndTrigger({
        element: this.el,
        callback: async () => {
          await this.fetch(input)
            .catch((error: Error) => {
              // Track error if fetching fails
              this.trackError(error);
            })
            .finally(() => {
              this.loading = false;
              this.initialized = true; // Mark as initialized after fetching
            });
        },
      });
    } catch (error) {
      this.trackError(error);
      this.loading = false;
      this.initialized = true; // Mark as initialized after fetching
    }
  }

  private get renderVendor() {
    const validations  = [
      this.el.getElementsByClassName('cbs-widget').length > 0,
      !this.data?.legs?.length , !(this.data?.legs && this.data.legs.every(l => isGameAvailableForBetting(l.game)))
    ]
    if (validations.some(Boolean)) {
      return;
    }
    const sidelines = document.createElement('div');
    sidelines.classList.add('cbs-widget');
    sidelines.setAttribute('data-config', JSON.stringify(this.getSidelinesData));
    // sidelines.innerHTML = `BET NOW`;
    this.el.appendChild(sidelines);
  }

  private get getInput() {
    const dataPick = this.getDataFromAttribute;
    if (dataPick.length > 0) return dataPick.map(d => toPickSnippetInput(d));
    return null;
  }

  // Remove typing and place correct one
  private get getDataFromAttribute() {
    const attributeData = JSON.parse(this.getDataPickAttribute);
    if (isUndefinedOrNull(attributeData) || !Array.isArray(attributeData)) {
      console.warn('Invalid or missing data-pick attribute on SportslinePickSnippetParlay component');
      return [];
    }
    return attributeData.map(d => getDataFromAttribute(JSON.stringify(d)));
  }

  private get getDataPickAttribute() {
    return this.el.getAttribute('data-pick') ?? null;
  }

  private get getLegs() {
    const attributeData = JSON.parse(this.getDataPickAttribute);
    if (isUndefinedOrNull(attributeData) || !Array.isArray(attributeData)) {
      console.warn('Invalid or missing data-pick attribute on SportslinePickSnippetParlay component');
      return [];
    }

    if (!this.data.legs) {
      console.warn('Legs data is missing in the response from the server');
      return [];
    }
    return this.data.legs.map((data, index) => {
      return fromPickSnippetToPickSnippetCardProps({
        data,
        dataAttr: JSON.stringify(attributeData[index]),
      });
    });
  }

  private get getSidelinesData() {
    return {
      type: 'parlay',
      markets: this.getLegs
        .map(l => {
          if (l.sideLinesData && typeof l.sideLinesData === 'object') {
            const { type, ...rest } = l.sideLinesData;
            return rest;
          } else {
            console.warn('Invalid or missing sideLinesData for leg:', l);
            return null; // Return null for any leg with invalid sideLinesData
          }
        })
        .filter(l => l !== null),
    };
  }

  render() {
    if (!this.initialized) return null; // Prevent rendering before initialization
    if (this.loading) {
      return (
        <Host>
          <div class="sportsline-pick-snippet-parlay--loading">
            <sportsline-circular-progress size="sm" />
          </div>
        </Host>
      );
    }
    if (Boolean(this.error) || !Boolean(this.data)) {
      return null;
    }

    return (
      <Host>
        <sportsline-pick-snippet-parlay-card odds={this.data.odds} wager={10} legs={this.getLegs}>
          {/* Not Pretty but works */}
          <slot>{this.renderVendor}</slot>
        </sportsline-pick-snippet-parlay-card>
      </Host>
    );
  }
}
